import { useState, useMemo } from 'react';
import { Card, Table, Button, Badge, Form, Row, Col, Spinner, Pagination } from 'react-bootstrap';
import type { Ingredient } from '../../../../models/Ingredient';

interface IngredientListProps {
  readonly ingredients: Ingredient[];
  readonly onEdit: (ingredient: Ingredient) => void;
  readonly onDelete: (ingredient: Ingredient) => void;
  readonly loading: boolean;
}

export const IngredientList = ({ ingredients, onEdit, onDelete, loading }: IngredientListProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'updateAt'>('updateAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(100);

  // Memoized filtered and sorted ingredients
  const { sortedIngredients, totalPages, paginatedIngredients } = useMemo(() => {
    const filtered = ingredients.filter(
      (ingredient) =>
        ingredient.ingredientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ingredient.engName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ingredient.korName.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const sorted = [...filtered].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      if (sortBy === 'updateAt') {
        aValue = new Date(a.updateAt).getTime();
        bValue = new Date(b.updateAt).getTime();
      } else {
        aValue = a.ingredientName.toLowerCase();
        bValue = b.ingredientName.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    const totalPages = Math.ceil(sorted.length / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginated = sorted.slice(startIndex, endIndex);

    return {
      sortedIngredients: sorted,
      totalPages,
      paginatedIngredients: paginated,
    };
  }, [ingredients, searchTerm, sortBy, sortOrder, currentPage, pageSize]);

  const handleSort = (column: 'name' | 'updateAt') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const getRiskBadgeVariant = (risk: string) => {
    const riskLevel = risk.toLowerCase();
    if (riskLevel.includes('1')) {
      return 'success';
    }
    if (riskLevel.includes('2')) {
      return 'warning';
    }
    if (riskLevel.includes('3')) {
      return 'danger';
    }
    return 'secondary';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <Card>
      <Card.Header>
        <Row className="align-items-center">
          <Col md={6}>
            <h5 className="mb-0">
              Ingredients ({sortedIngredients.length})
              {totalPages > 1 && (
                <small className="text-muted ms-2">
                  Page {currentPage} of {totalPages} ({pageSize} per page)
                </small>
              )}
            </h5>
          </Col>
          <Col md={6}>
            <Form.Control
              type="text"
              placeholder="Search ingredients..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </Col>
        </Row>
      </Card.Header>
      <Card.Body className="p-0">
        {sortedIngredients.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-muted mb-0">
              {searchTerm ? 'No ingredients found matching your search.' : 'No ingredients found.'}
            </p>
          </div>
        ) : (
          <>
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer', width: '15%' }} onClick={() => handleSort('name')}>
                    Ingredient Name {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{ width: '8%' }}>Risk</th>
                  <th style={{ width: '12%' }}>English Name</th>
                  <th style={{ width: '12%' }}>Korean Name</th>
                  <th style={{ width: '20%' }}>Efficacy</th>
                  <th style={{ width: '8%' }}>Version</th>
                  <th style={{ cursor: 'pointer', width: '15%' }} onClick={() => handleSort('updateAt')}>
                    Updated At {sortBy === 'updateAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{ width: '10%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedIngredients.map((ingredient) => (
                  <tr key={`${ingredient.ingredientName}-${ingredient.version}`}>
                    <td style={{ maxWidth: '150px' }}>
                      <div className="text-truncate">
                        <strong title={ingredient.ingredientName}>
                          {ingredient.ingredientName.length > 20
                            ? `${ingredient.ingredientName.substring(0, 20)}...`
                            : ingredient.ingredientName}
                        </strong>
                      </div>
                      {ingredient.description && (
                        <div className="text-muted small mt-1">
                          {ingredient.description.length > 30
                            ? `${ingredient.description.substring(0, 30)}...`
                            : ingredient.description}
                        </div>
                      )}
                    </td>
                    <td>
                      <Badge bg={getRiskBadgeVariant(ingredient.risk)}>{ingredient.risk}</Badge>
                    </td>
                    <td className="text-muted small" style={{ maxWidth: '120px' }}>
                      <div className="text-truncate" title={ingredient.engName}>
                        {ingredient.engName.length > 15
                          ? `${ingredient.engName.substring(0, 15)}...`
                          : ingredient.engName}
                      </div>
                    </td>
                    <td className="text-muted small" style={{ maxWidth: '120px' }}>
                      <div className="text-truncate" title={ingredient.korName}>
                        {ingredient.korName}
                      </div>
                    </td>
                    <td>
                      <span className="badge bg-light text-dark">{ingredient.efficacy}</span>
                      {ingredient.efficacyDescription && (
                        <div className="text-muted small mt-1">
                          {ingredient.efficacyDescription.length > 40
                            ? `${ingredient.efficacyDescription.substring(0, 40)}...`
                            : ingredient.efficacyDescription}
                        </div>
                      )}
                    </td>
                    <td>
                      <Badge bg="info">{ingredient.version}</Badge>
                    </td>
                    <td className="text-muted small">{formatDate(ingredient.updateAt)}</td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button variant="outline-primary" size="sm" onClick={() => onEdit(ingredient)}>
                          Edit
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => onDelete(ingredient)}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="text-muted small">
                  Showing {(currentPage - 1) * pageSize + 1} to{' '}
                  {Math.min(currentPage * pageSize, sortedIngredients.length)} of {sortedIngredients.length} ingredients
                </div>
                <Pagination className="mb-0">
                  <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                  <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />

                  {/* Show page numbers */}
                  {(() => {
                    const pages = [];
                    const startPage = Math.max(1, currentPage - 2);
                    const endPage = Math.min(totalPages, currentPage + 2);

                    if (startPage > 1) {
                      pages.push(
                        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                          1
                        </Pagination.Item>,
                      );
                      if (startPage > 2) {
                        pages.push(<Pagination.Ellipsis key="start-ellipsis" />);
                      }
                    }

                    for (let page = startPage; page <= endPage; page++) {
                      pages.push(
                        <Pagination.Item
                          key={page}
                          active={page === currentPage}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </Pagination.Item>,
                      );
                    }

                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(<Pagination.Ellipsis key="end-ellipsis" />);
                      }
                      pages.push(
                        <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                          {totalPages}
                        </Pagination.Item>,
                      );
                    }

                    return pages;
                  })()}

                  <Pagination.Next
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  />
                  <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                </Pagination>
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};
